import type { HTMLAttributes, ReactNode } from 'react'

export type EyebrowTone = 'cyan' | 'green' | 'magenta' | 'purple' | 'muted'

interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Mono uppercase kicker above the content. */
  eyebrow?: ReactNode
  /**
   * Muted by default. Cyan is the ONE accent kicker a screen gets, so it is
   * opt-in: pass `active` (a live card) or `eyebrowTone="cyan"` for it.
   */
  eyebrowTone?: EyebrowTone
  /** Cyan border + glow. Reserved for live or highlighted state, never decoration. */
  active?: boolean
  tight?: boolean
  /**
   * A card inside a card, or a list container: flat `--fill-soft`, no gradient,
   * no shadow.
   *
   * NEUTRAL text and graphics only. A toned `Pill` inside a flat card that
   * itself sits in a card is two `--fill-soft` layers deep, and there the tones
   * measure 4.42:1 (light, at the `--surface-2` end of the card gradient) and
   * 4.22:1 for purple in dark — under the 4.5 an 11px pill label needs. Put the
   * pill on the card, not on the flat panel inside it.
   */
  flat?: boolean
  /**
   * A mono counter-line opposite the eyebrow. Rendering it puts the eyebrow in
   * a `.card__head` row; without `meta` or `head` the eyebrow stays bare.
   */
  meta?: ReactNode
  /** Anything else on the eyebrow's baseline — an action, a pill. */
  head?: ReactNode
  as?: 'div' | 'section' | 'article' | 'li'
}

export function Card({
  eyebrow,
  eyebrowTone = 'muted',
  active = false,
  tight = false,
  flat = false,
  meta,
  head,
  as: Tag = 'div',
  children,
  className,
  ...rest
}: CardProps) {
  const classes = [
    'card',
    active && 'card--active',
    tight && 'card--tight',
    flat && 'card--flat',
    className,
  ].filter(Boolean).join(' ')

  // ALWAYS the base class: it carries the font, size, letter-spacing and
  // margin, and the modifiers are colour-only. A branch that emitted
  // `card__eyebrow--green` alone would lose the typography with the colour.
  // `cyan` maps to `--accent` rather than to a bare class, because bare is now
  // muted.
  const eyebrowClass = [
    'card__eyebrow',
    eyebrowTone === 'cyan' && 'card__eyebrow--accent',
    eyebrowTone !== 'cyan' && eyebrowTone !== 'muted' && `card__eyebrow--${eyebrowTone}`,
  ].filter(Boolean).join(' ')

  const eyebrowEl = eyebrow != null ? <div className={eyebrowClass}>{eyebrow}</div> : null

  return (
    <Tag {...rest} className={classes}>
      {meta != null || head != null ? (
        <div className="card__head">
          {eyebrowEl}
          {meta != null && <span className="card__meta">{meta}</span>}
          {head}
        </div>
      ) : (
        eyebrowEl
      )}
      {children}
    </Tag>
  )
}
